import React, { useState, useEffect, useMemo, useRef } from "react";
import { Row, Col } from "reactstrap";
import { connect } from "react-redux";
import LoadingOverlay from "react-loading-overlay";
import Loader from "react-loaders";
import BackButton from "../../../../designUtils/Logo/BackButton.svg";
import FetchAmazonNotificationLogsService from "../../../../Service/DashboardServices/AmazonNotificationsServices/FetchAmazonNotificationLogsService";
import FetchAmazonNotificationIssuesService from "../../../../Service/DashboardServices/AmazonNotificationsServices/FetchAmazonNotificationIssuesService";
import SaveNotificationTypeLookupService from "../../../../Service/DashboardServices/AmazonNotificationsServices/SaveNotificationTypeLookupService";
import CustomModal from "../../../Components/Modals/CustomModal";
import ReactTable from "react-table";
import NotificationDetailsDrawer from "./Components/NotificationDetailsDrawer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSync, faRedo } from "@fortawesome/free-solid-svg-icons";
import ClassificationMatrixOverview from "./Components/ClassificationMatrixOverview";
import StoreListService from "../../../../Service/DashboardServices/AccountOverviewServices/StoreServices/StoreListService";
import {
    buildNotificationGuide, getClassification, mapApiNotification, getIssueIdentifier, payloadSnippet,
    severityBadgeClass, statusBadgeClass, entityBadgeClass, isWithinDateFilter,
    getLookupOptions, getLookupLabel, severityBucket
} from "./Components/NotificationMonitoring";

function formatLastUpdated(value) {
    if (!value) {
        return "-";
    }
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) {
        return "-";
    }
    const day = date.getDate();
    const month = date.toLocaleString("en-GB", { month: "short" });
    const year = date.getFullYear();
    const time = date.toLocaleString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
    });
    return `${day} ${month} ${year}, ${time}`;
}


function AmazonNotificationsDashboard(props) {
    const [isLoading, setIsLoading] = useState(false);
    const [notificationData, setNotificationData] = useState([]);
    const [search, setSearch] = useState("");
    const [guide, setGuide] = useState([]);
    const [showHelp, setShowHelp] = useState(false);
    const [selectedStoreName, setSelectedStoreName] = useState(props.storeName || "");
    const [selectedStoreIntegerID, setSelectedStoreIntegerID] = useState(props.storeIntegerID || undefined);
    const [storesByNotificationCount, setStoresByNotificationCount] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);
    const [typeCounts, setTypeCounts] = useState({});
    const [itemSummaryData, setItemSummaryData] = useState([]);
    const [itemSummarySearch, setItemSummarySearch] = useState("");
    const [noficationsActions, setNoficationsActions] = useState([]);
    const [selectedItemKey, setSelectedItemKey] = useState(null);
    const [drawerItem, setDrawerItem] = useState(null);
    const [statusOverrides, setStatusOverrides] = useState({});
    const [filterSeverity, setFilterSeverity] = useState("");
    const [filterBusinessImpact, setFilterBusinessImpact] = useState("");
    const [filterNeedsAttention, setFilterNeedsAttention] = useState("");
    const [filterStatus, setFilterStatus] = useState("");
    const [filterType, setFilterType] = useState("");
    const [filterDate, setFilterDate] = useState("");
    const [payloadItem, setPayloadItem] = useState(null);
    const [notificationTab, setNotificationTab] = useState("today");
    const [settingsTab, setSettingsTab] = useState("types");
    const [settingsDraft, setSettingsDraft] = useState([]);
    const [pendingLookupChanges, setPendingLookupChanges] = useState({});
    const [isSavingSettings, setIsSavingSettings] = useState(false);
    const issuesSectionRef = useRef(null);
    const fetchSeqRef = useRef(0);
    const issuesFetchSeqRef = useRef(0);
    const issuesSearchTimerRef = useRef(null);
    const pageSize = 2000;

    const cleanId = (value) => {
        if (!value || value === "-") {
            return "";
        }
        return value;
    };

    useEffect(() => {
        if (props.token && (!props.adProfiles || props.adProfiles.length === 0)) {
            props.dispatch(StoreListService(props.token));
        }
        // Always load the store with the most notifications (backend auto-picks)
        fetchSummary(undefined, "today");
    }, [props.token]);

    const fetchSummary = (
        storeId = selectedStoreIntegerID,
        tab = notificationTab,
        options = {}
    ) => {
        const showOverlay = options.showOverlay !== false;
        const fetchSeq = ++fetchSeqRef.current;
        if (showOverlay) {
            setIsLoading(true);
        }
        setNotificationData([]);
        setSelectedItemKey(null);
        const requestData = {
            page: 1,
            pageSize: 1,
            search: "",
            notificationTab: tab,
            includeItemSummary: true,
            includeSettings: true,
            includeNotificationList: false,
        };
        if (storeId !== undefined && storeId !== null && storeId !== "") {
            requestData.integerID = storeId;
        }
        FetchAmazonNotificationLogsService(props.token, requestData).then((response) => {
            if (fetchSeq !== fetchSeqRef.current) {
                return;
            }
            if (response && response.status) {
                const selectedStore = response.selectedStore || {};
                if (selectedStore.integerID != null && selectedStore.integerID !== "") {
                    setSelectedStoreIntegerID(selectedStore.integerID);
                    setSelectedStoreName(selectedStore.name || "");
                }
                setStoresByNotificationCount(
                    response.storesByNotificationCount || []
                );
                setTotalRecords(response.totalRecords || 0);
                setTypeCounts(response.typeCounts || {});
                setItemSummaryData(response.itemSummary || []);
                setNoficationsActions(response.noficationsActions || []);
                setGuide(
                    buildNotificationGuide(
                        response.notificationTypeSettings || []
                    )
                );
            }
            setIsLoading(false);
        });
    };

    const fetchIssues = (
        selectedItem,
        page = 0,
        searchText = "",
        storeId = selectedStoreIntegerID,
        tab = notificationTab,
        options = {}
    ) => {
        const q = (searchText || "").trim();
        // Empty search needs a selected summary row; non-empty search is store-wide.
        if (!q && !selectedItem) {
            setNotificationData([]);
            return;
        }
        const fetchSeq = ++issuesFetchSeqRef.current;
        const payload = {
            page: page + 1,
            pageSize,
            search: q,
            integerID: storeId,
            notificationTab: tab,
            entityType: "",
            asin: "",
            sku: "",
            sellerId: "",
        };
        if (!q && selectedItem) {
            payload.entityType = selectedItem.entityType || "";
            payload.asin = cleanId(selectedItem.asin);
            payload.sku = cleanId(selectedItem.sku);
            payload.sellerId = cleanId(selectedItem.sellerId);
        }
        FetchAmazonNotificationIssuesService(props.token, payload).then((response) => {
            if (fetchSeq !== issuesFetchSeqRef.current) {
                return;
            }
            if (response && response.status) {
                setNotificationData(response.notificationList || []);
            }
        });
    };
    
    const handleGuideChange = (index, field, selectedKey, notificationType) => {
        const fieldConfig = {
            severity: {
                entity: "Amazon_Notifications_Severity",
                dbField: "severity",
                keyProp: "severityKey",
                labelProp: "severity",
            },
            businessImpact: {
                entity: "Amazon_Notifications_Business_Impact",
                dbField: "businessImpact",
                keyProp: "businessImpactKey",
                labelProp: "businessImpact",
            },
            needsAttention: {
                entity: "Amazon_Notifications_Attention",
                dbField: "attention",
                keyProp: "needsAttentionKey",
                labelProp: "needsAttention",
            },
        };
        const config = fieldConfig[field];
        if (!config) {
            return;
        }
        const itemType = notificationType || (settingsDraft[index] && settingsDraft[index].type);
        const lookup = (noficationsActions || []).find(
            (a) =>
                a.entity === config.entity &&
                String(a.key) === String(selectedKey)
        );
        const label = getLookupLabel(
            noficationsActions,
            config.entity,
            selectedKey
        );
        const original = (guide || []).find((row) => row.type === itemType);
        const originalKey = original ? original[config.keyProp] : "";
        const changeKey = `${itemType}|${config.dbField}`;
        setSettingsDraft((prev) =>
            prev.map((row) =>
                row.type === itemType
                    ? {
                        ...row,
                        [config.keyProp]: selectedKey,
                        [config.labelProp]: label
                    }
                    : row
            )
        );
        setPendingLookupChanges((prev) => {
            const next = { ...prev };
            if (String(selectedKey || "") === String(originalKey || "")) {
                delete next[changeKey];
            } else {
                next[changeKey] = {
                    entity: config.entity,
                    field: config.dbField,
                    key: selectedKey,
                    notification_type: itemType,
                    lookup_values_id: lookup && lookup.id,
                };
            }
            return next;
        });
    };

    const openNotificationSettings = () => {
        setSettingsTab("types");
        setSettingsDraft(guide || []);
        setPendingLookupChanges({});
        setShowHelp(true);
    };

    const handleSaveSettings = () => {
        const changes = Object.values(pendingLookupChanges);
        if (!changes.length || isSavingSettings) {
            return;
        }
        setIsSavingSettings(true);
        SaveNotificationTypeLookupService(props.token, changes).then((result) => {
            setIsSavingSettings(false);
            if (result) {
                setGuide(settingsDraft);
                setPendingLookupChanges({});
            }
        });
    };

    const mappedNotifications = useMemo(() => {
        return (notificationData || []).map(
            (row, index) => {
                const mapped = mapApiNotification(
                    row,
                    index
                );
                if (statusOverrides[mapped.id]) {
                    mapped.status =
                        statusOverrides[mapped.id];
                }
                return mapped;
            }
        );
    }, [
        notificationData,
        statusOverrides
    ]);

    const storeOptions = useMemo(() => {
        const list = [];
        const seen = new Set();
        const countById = {};
        (storesByNotificationCount || []).forEach((row) => {
            if (row && row.integerID != null && row.integerID !== "") {
                countById[String(row.integerID)] = row.notificationCount || 0;
            }
        });

        if (props.adProfiles && Array.isArray(props.adProfiles)) {
            props.adProfiles.forEach((store) => {
                if (store && store.name && !seen.has(store.name)) {
                    seen.add(store.name);
                    list.push({
                        name: store.name,
                        integerID: store.integerID,
                        location: store.location,
                        notificationCount:
                            countById[String(store.integerID)] || 0,
                    });
                }
            });
        }

        (storesByNotificationCount || []).forEach((row) => {
            if (row && row.name && !seen.has(row.name)) {
                seen.add(row.name);
                list.push({
                    name: row.name,
                    integerID: row.integerID,
                    location: "",
                    notificationCount: row.notificationCount || 0,
                });
            }
        });

        (mappedNotifications || []).forEach((n) => {
            if (n.store && !seen.has(n.store)) {
                seen.add(n.store);
                list.push({
                    name: n.store,
                    integerID: null,
                    location: "",
                    notificationCount: 0,
                });
            }
        });

        if (props.storeName && !seen.has(props.storeName)) {
            seen.add(props.storeName);
            list.push({
                name: props.storeName,
                integerID: props.storeIntegerID || null,
                location: "",
                notificationCount:
                    countById[String(props.storeIntegerID)] || 0,
            });
        }

        list.sort((a, b) => {
            const countDiff =
                (b.notificationCount || 0) - (a.notificationCount || 0);
            if (countDiff !== 0) {
                return countDiff;
            }
            return (a.name || "").localeCompare(b.name || "");
        });
        return list;
    }, [
        props.adProfiles,
        mappedNotifications,
        props.storeName,
        props.storeIntegerID,
        storesByNotificationCount,
    ]);


    const activeStore = selectedStoreName || "";

    const handleStoreChange = (e) => {
        const storeName = e.target.value;
        if (!storeName) {
            return;
        }
        const store = (storeOptions || []).find((s) => s.name === storeName);
        if (!store || store.integerID == null || store.integerID === "") {
            return;
        }
        setSelectedStoreName(store.name);
        setSelectedStoreIntegerID(store.integerID);
        setCurrentPage(0);
        setSelectedItemKey(null);
        setItemSummaryData([]);
        setNotificationData([]);
        setTypeCounts({});
        setTotalRecords(0);
        fetchSummary(store.integerID, notificationTab);
    };

    const itemSummary = useMemo(() => {
        return (itemSummaryData || []).map((item) => {
            let critical = 0;
            let high = 0;
            let medium = 0;
            let low = 0;
            const typeCountsByType = item.typeCounts || {};
            const typeKeys = Object.keys(typeCountsByType);

            if (typeKeys.length) {
                typeKeys.forEach((type) => {
                    const count = typeCountsByType[type] || 0;
                    const classification = getClassification(
                        type,
                        guide,
                        noficationsActions
                    );
                    const bucket = severityBucket(classification.severity);
                    if (bucket === "critical") {
                        critical += count;
                    } else if (bucket === "high") {
                        high += count;
                    } else if (bucket === "medium") {
                        medium += count;
                    } else {
                        low += count;
                    }
                });
            } else {
                // Fallback to API-precomputed buckets
                critical = item.critical || 0;
                high = item.high || 0;
                medium = item.medium || 0;
                low = item.low || 0;
            }

            return {
                ...item,
                asin: item.asin || "-",
                sku: item.sku || "-",
                sellerId: item.sellerId || "-",
                types: item.types || [],
                type: item.type || (item.types || []).join(", "),
                total: item.total || 0,
                critical,
                high,
                medium,
                low,
            };
        });
    }, [
        itemSummaryData,
        guide,
        noficationsActions
    ]);

    const filteredItemSummary = useMemo(() => {
        const q = (itemSummarySearch || "").trim().toLowerCase();
        if (!q) {
            return itemSummary;
        }
        return (itemSummary || []).filter((item) => {
            const haystack = [
                item.entityType,
                item.asin,
                item.sku,
                item.sellerId,
                item.type,
                item.total,
                item.critical,
                item.high,
                item.medium,
                item.low,
            ]
                .join(" ")
                .toLowerCase();
            return haystack.includes(q);
        });
    }, [itemSummary, itemSummarySearch]);

    useEffect(() => {
        const first = (filteredItemSummary && filteredItemSummary[0]) || null;
        if (!first) {
            if (selectedItemKey) {
                setSelectedItemKey(null);
            }
            return;
        }

        const stillValid =
            selectedItemKey &&
            filteredItemSummary.some(
                (item) => item.rowKey === selectedItemKey
            );
        if (stillValid) {
            return;
        }

        setSelectedItemKey(first.rowKey);
        setCurrentPage(0);
        fetchIssues(first, 0, "", selectedStoreIntegerID, notificationTab, {
            showOverlay: true,
        });
    }, [filteredItemSummary, selectedItemKey, notificationTab]);

    const uniqueTypes = useMemo(() => {
        const types = [];
        (itemSummary || []).forEach((item) => {
            (item.types || []).forEach((type) => {
                if (type && types.indexOf(type) === -1) {
                    types.push(type);
                }
            });
        });
        return types.sort((a, b) =>
            String(a).localeCompare(String(b), undefined, {
                sensitivity: "base",
            })
        );
    }, [
        itemSummary
    ]);

    const filteredIssues = useMemo(() => {

        const q = (search || "").trim().toLowerCase();

        const matches = mappedNotifications.filter((n, index) => {
            if (!q && selectedItemKey) {
                const identifier = getIssueIdentifier(n, index);
                if (identifier.rowKey !== selectedItemKey) {
                    return false;
                }
            }

            const classification = getClassification(n.type, guide, noficationsActions);

            if (q) {
                const haystack = [
                    n.asin,
                    n.sku,
                    n.sellerId,
                    n.identifierType,
                    n.type,
                    n.status,
                    n.payload && JSON.stringify(n.payload),
                    classification.severity,
                    classification.businessImpact,
                    classification.needsAttention,
                    classification.recommendedAction,
                ]
                    .join(" ")
                    .toLowerCase();
                if (!haystack.includes(q)) {
                    return false;
                }
            }

            if (
                filterSeverity &&
                String(classification.severity || "") !==
                    String(filterSeverity)
            ) {
                return false;
            }

            if (
                filterBusinessImpact &&
                String(classification.businessImpact || "") !==
                    String(filterBusinessImpact)
            ) {
                return false;
            }

            if (
                filterNeedsAttention &&
                String(classification.needsAttention || "") !==
                    String(filterNeedsAttention)
            ) {
                return false;
            }

            if (
                filterStatus &&
                String(n.status || "").toUpperCase() !==
                    String(filterStatus).toUpperCase()
            ) {
                return false;
            }

            if (
                filterType &&
                n.type !== filterType
            ) {
                return false;
            }

            if (
                !isWithinDateFilter(
                    n.eventTimeRaw,
                    filterDate
                )
            ) {
                return false;
            }

            return true;
        });

        return matches;

    }, [
        mappedNotifications,
        selectedItemKey,
        search,
        guide,
        noficationsActions,
        filterSeverity,
        filterBusinessImpact,
        filterNeedsAttention,
        filterStatus,
        filterType,
        filterDate
    ]);

    const lastUpdatedLabel = useMemo(() => {
        let latest = null;
        const consider = (raw) => {
            if (!raw) {
                return;
            }
            const date = new Date(raw);
            if (isNaN(date.getTime())) {
                return;
            }
            if (!latest || date > latest) {
                latest = date;
            }
        };
        (itemSummaryData || []).forEach((item) => {
            const details = item.typeDetails || {};
            Object.keys(details).forEach((type) => {
                consider(details[type] && details[type].eventTime);
            });
        });
        (mappedNotifications || []).forEach((n) => {
            consider(n.eventTimeRaw);
        });
        return formatLastUpdated(latest);
    }, [itemSummaryData, mappedNotifications]);

    const switchNotificationTab = (tab) => {
        if (tab === notificationTab) {
            return;
        }
        setNotificationTab(tab);
        setCurrentPage(0);
        setSelectedItemKey(null);
        setItemSummaryData([]);
        setNotificationData([]);
        setTypeCounts({});
        setTotalRecords(0);
        // Re-pick the max-volume store for this tab
        fetchSummary(undefined, tab);
    };

    const handleRefresh = () => {
        const selectedItem =
            (itemSummary || []).find(
                (item) => item.rowKey === selectedItemKey
            ) || null;
        fetchSummary(selectedStoreIntegerID, notificationTab, {
            showOverlay: true,
        });
        if (selectedItem) {
            fetchIssues(
                selectedItem,
                currentPage,
                search,
                selectedStoreIntegerID,
                notificationTab,
                { showOverlay: true }
            );
        }
    };

    const kpi = useMemo(() => {

        let criticalCount = 0;
        let highCount = 0;
        let needsAttentionCount = 0;

        Object.entries(typeCounts).forEach(([type, count]) => {
            const c = getClassification(type, guide, noficationsActions);
            const bucket = severityBucket(c.severity);
            if (bucket === "critical") criticalCount += count;
            if (bucket === "high") highCount += count;
            if (c.needsAttention === "Yes") needsAttentionCount += count;
        });

        return {
            total: totalRecords,
            criticalCount,
            highCount,
            needsAttentionCount
        };

    }, [
        typeCounts,
        totalRecords,
        guide,
        noficationsActions
    ]);

    const updateIssueStatus = (
        newStatus
    ) => {

        if (!drawerItem) {
            return;
        }

        setStatusOverrides(
            (prev) => ({
                ...prev,
                [drawerItem.id]:
                    newStatus
            })
        );

        setDrawerItem(null);
    };

    const itemSummaryColumns = useMemo(
        () => [
            {
                Header: "Identifier Type",
                accessor: "entityType",
                Cell: (cellInfo) => (
                    <span
                        className={`anm-badge ${entityBadgeClass(
                            cellInfo.value
                        )}`}
                    >
                        {cellInfo.value}
                    </span>
                ),
            },
            {
                Header: "ASIN",
                accessor: "asin",
                Cell: (cellInfo) =>
                    cellInfo.value !== "-" ? (
                        cellInfo.value
                    ) : (
                        <span className="anm-muted">-</span>
                    ),
            },
            {
                Header: "SKU",
                accessor: "sku",
                Cell: (cellInfo) =>
                    cellInfo.value !== "-" ? (
                        cellInfo.value
                    ) : (
                        <span className="anm-muted">-</span>
                    ),
            },
            {
                Header: "Seller ID",
                accessor: "sellerId",
                Cell: (cellInfo) =>
                    cellInfo.value !== "-" ? (
                        <span className="anm-seller-id">
                            {cellInfo.value}
                        </span>
                    ) : (
                        <span className="anm-muted">-</span>
                    ),
            },
            {
                Header: "Total Issues",
                accessor: "total",
                width: 100,
                Cell: (cellInfo) => cellInfo.value,
            },
            {
                Header: "Critical",
                accessor: "critical",
                width: 90,
                Cell: (cellInfo) => (
                    <span className="anm-badge anm-badge-critical">
                        {cellInfo.value}
                    </span>
                ),
            },
            {
                Header: "High",
                accessor: "high",
                width: 80,
                Cell: (cellInfo) => (
                    <span className="anm-badge anm-badge-high">
                        {cellInfo.value}
                    </span>
                ),
            },
            {
                Header: "Medium",
                accessor: "medium",
                width: 90,
                Cell: (cellInfo) => (
                    <span className="anm-badge anm-badge-medium">
                        {cellInfo.value}
                    </span>
                ),
            },
            {
                Header: "Low",
                accessor: "low",
                width: 80,
                Cell: (cellInfo) => (
                    <span className="anm-badge anm-badge-low">
                        {cellInfo.value}
                    </span>
                ),
            },
            {
                Header: "Active Types",
                accessor: "type",
                Cell: (cellInfo) => (
                    <span
                        className="anm-types"
                        title={cellInfo.value || ""}
                    >
                        {cellInfo.value}
                    </span>
                ),
            },
        ],
        []
    );

    const issueColumns = useMemo(
        () => [
            {
                Header: "Target Entity (ASIN / SKU / Seller ID)",
                id: "targetEntity",
                minWidth: 170,
                Cell: (cellInfo) => {
                    const item = cellInfo.original;

                    if (item.asin) {
                        return (
                            <span>
                                <strong>{item.asin}</strong>
                                <br />
                                <span className="anm-small-muted">
                                    SKU: {item.sku || "-"}
                                </span>
                            </span>
                        );
                    }

                    if (item.sku) {
                        return (
                            <span>
                                <span className="anm-no-asin">
                                    No ASIN
                                </span>
                                <br />
                                <strong className="anm-sku">
                                    SKU: {item.sku}
                                </strong>
                            </span>
                        );
                    }

                    return (
                        <span>
                            <span className="anm-badge anm-badge-critical">
                                Seller ID
                            </span>
                            <br />
                            <strong className="anm-seller-id">
                                {item.sellerId || "-"}
                            </strong>
                        </span>
                    );
                },
            },
            {
                Header: "Notification Type",
                accessor: "type",
                minWidth: 180,
                style: { whiteSpace: "normal", overflow: "visible" },
                Cell: (cellInfo) => (
                    <span
                        className="anm-settings-type"
                        title={cellInfo.value || ""}
                    >
                        {cellInfo.value}
                    </span>
                ),
            },
            {
                Header: "Notification Payload",
                id: "payload",
                minWidth: 180,
                sortable: false,
                Cell: (cellInfo) => (
                    <span>
                        <span className="anm-payload-snippet">
                            {payloadSnippet(
                                cellInfo.original.payload
                            )}
                        </span>
                        <button
                            type="button"
                            className="anm-action-btn"
                            onClick={() => setPayloadItem(cellInfo.original)}
                        >
                            <i className="far fa-eye" /> View Payload
                        </button>
                    </span>
                ),
            },
            {
                Header: "Severity",
                id: "severity",
                width: 110,
                Cell: (cellInfo) => {
                    const classification = getClassification(
                        cellInfo.original.type,
                        guide,
                        noficationsActions
                    );
                    return (
                        <span
                            className={`anm-badge ${severityBadgeClass(
                                classification.severity
                            )}`}
                        >
                            {classification.severity}
                        </span>
                    );
                },
            },
            {
                Header: "Business Impact",
                id: "businessImpact",
                width: 130,
                Cell: (cellInfo) => {
                    const classification = getClassification(
                        cellInfo.original.type,
                        guide,
                        noficationsActions
                    );
                    return (
                        <span className="anm-business-impact">
                            {classification.businessImpact}
                        </span>
                    );
                },
            },
            {
                Header: "Needs Attention",
                id: "needsAttention",
                width: 130,
                Cell: (cellInfo) => {
                    const classification = getClassification(
                        cellInfo.original.type,
                        guide,
                        noficationsActions
                    );
                    return (
                        <span
                            className={`anm-badge ${classification.needsAttention ===
                                    "Yes"
                                    ? "anm-badge-attention-yes"
                                    : "anm-badge-attention-no"
                                }`}
                        >
                            {classification.needsAttention}
                        </span>
                    );
                },
            },
            // {
            //     Header: "Status",
            //     accessor: "status",
            //     width: 120,
            //     Cell: (cellInfo) => (
            //         <span
            //             className={`anm-badge ${statusBadgeClass(
            //                 cellInfo.value
            //             )}`}
            //         >
            //             {cellInfo.value}
            //         </span>
            //     ),
            // },
            {
                Header: "Recommended Action",
                id: "recommendedAction",
                minWidth: 180,
                Cell: (cellInfo) => {
                    const classification = getClassification(
                        cellInfo.original.type,
                        guide,
                        noficationsActions
                    );
                    return (
                        <span
                            className="anm-recommended-text"
                            title={
                                classification.recommendedAction
                            }
                        >
                            {classification.recommendedAction}
                        </span>
                    );
                },
            },
            {
                Header: "Action",
                id: "viewAction",
                width: 100,
                sortable: false,
                Cell: (cellInfo) => (
                    <button
                        type="button"
                        className="anm-action-btn"
                        onClick={() =>
                            setDrawerItem(cellInfo.original)
                        }
                    >
                        <i className="far fa-eye" /> View
                    </button>
                ),
            },
            {
                Header: "Event Time",
                accessor: "eventTime",
                minWidth: 110,
                Cell: (cellInfo) => {
                    const raw =
                        cellInfo.original.eventTimeRaw ||
                        cellInfo.value;
                    if (!raw || raw === "-") {
                        return "-";
                    }
                    const str = String(raw);
                    const datePart = str.slice(0, 10);
                    const timeMatch = str.match(/[T ](\d{2}:\d{2})/);
                    const timePart = timeMatch ? timeMatch[1] : "";
                    return (
                        <span className="anm-event-time">
                            {datePart}
                            {timePart ? (
                                <>
                                    <br />
                                    {timePart}
                                </>
                            ) : null}
                        </span>
                    );
                },
            },
        ],
        [guide, noficationsActions]
    );

    const guideColumns = [
        {
            Header: "Notification Type",
            accessor: "type",
            width: 280,
            style: { whiteSpace: "normal", overflow: "visible" },
            Cell: (cellInfo) => (
                <span
                    className="anm-settings-type"
                    title={cellInfo.value || ""}
                >
                    {cellInfo.value}
                </span>
            ),
        },
        {
            Header: "Category",
            accessor: "category",
            width: 165
        },
        {
            Header: "Severity",
            accessor: "severityKey",
            width: 140,
            Cell: (cellInfo) => (
                <select
                    className="form-control form-control-sm"
                    value={cellInfo.value || ""}
                    onChange={(e) =>
                        handleGuideChange(
                            cellInfo.index,
                            "severity",
                            e.target.value,
                            cellInfo.original && cellInfo.original.type
                        )
                    }
                >
                    <option value="">-- Select --</option>
                    {getLookupOptions(
                        noficationsActions,
                        "Amazon_Notifications_Severity"
                    ).map((a) => (
                        <option key={a.id || a.key} value={String(a.key)}>
                            {getLookupLabel(
                                noficationsActions,
                                "Amazon_Notifications_Severity",
                                a.key
                            )}
                        </option>
                    ))}
                </select>
            )
        },
        {
            Header: "Business Impact",
            accessor: "businessImpactKey",
            width: 165,
            Cell: (cellInfo) => (
                <select
                    className="form-control form-control-sm"
                    value={cellInfo.value || ""}
                    onChange={(e) =>
                        handleGuideChange(
                            cellInfo.index,
                            "businessImpact",
                            e.target.value,
                            cellInfo.original && cellInfo.original.type
                        )
                    }
                >
                    <option value="">-- Select --</option>
                    {getLookupOptions(
                        noficationsActions,
                        "Amazon_Notifications_Business_Impact"
                    ).map((a) => (
                        <option key={a.id || a.key} value={String(a.key)}>
                            {getLookupLabel(
                                noficationsActions,
                                "Amazon_Notifications_Business_Impact",
                                a.key
                            )}
                        </option>
                    ))}
                </select>
            )
        },
        {
            Header: "Attention",
            accessor: "needsAttentionKey",
            width: 140,
            Cell: (cellInfo) => (
                <select
                    className="form-control form-control-sm"
                    value={cellInfo.value || ""}
                    onChange={(e) =>
                        handleGuideChange(
                            cellInfo.index,
                            "needsAttention",
                            e.target.value,
                            cellInfo.original && cellInfo.original.type
                        )
                    }
                >
                    <option value="">-- Select --</option>
                    {getLookupOptions(
                        noficationsActions,
                        "Amazon_Notifications_Attention"
                    ).map((a) => (
                        <option key={a.id || a.key} value={String(a.key)}>
                            {getLookupLabel(
                                noficationsActions,
                                "Amazon_Notifications_Attention",
                                a.key
                            )}
                        </option>
                    ))}
                </select>
            )
        },
        {
            Header: "Recommended Action",
            accessor: "recommendedAction",
            minWidth: 260
        }
    ];

    return (
        <div className="anm-monitor mt-5">
            {/* Header */}
            <Row className="align-items-center mb-2 mt-2">
                <Col md={12} className="d-flex align-items-center">

                    <button
                        type="button"
                        className="amazon-notification-back-button-header d-flex align-items-center"
                        onClick={props.onBackHeader}
                    >
                        <img
                            src={BackButton}
                            alt="Back"
                        />
                    </button>

                    <span className="amazon-notification-title ml-2">
                        Amazon Notification Monitoring
                    </span>

                </Col>
            </Row>

            {/* Notification Tabs */}
            <Row className="mt-3 mb-3">
                <Col md={12}>
                    <div className="brand-tabs d-flex align-items-center">

                        <div
                            className={`main-tabs ${
                                notificationTab === "today" ? "active" : ""
                            }`}
                            onClick={() => switchNotificationTab("today")}
                        >
                            Today's Notifications
                        </div>

                        <div
                            className={`main-tabs ${
                                notificationTab === "previous" ? "active" : ""
                            }`}
                            onClick={() => switchNotificationTab("previous")}
                        >
                            Previous Notifications
                        </div>

                        <div className="ml-auto d-flex align-items-center anm-header-actions">
                            <div className="anm-meta-chip">
                                <FontAwesomeIcon icon={faSync} className="anm-meta-chip-icon" />
                                Last Updated: {lastUpdatedLabel}
                            </div>
                            <button
                                type="button"
                                className="anm-meta-chip anm-refresh-btn"
                                onClick={handleRefresh}
                            >
                                <FontAwesomeIcon icon={faRedo} className="anm-meta-chip-icon" />
                                Refresh
                            </button>

                            <button
                                type="button"
                                className="bulk-creation-button anm-settings-btn"
                                onClick={openNotificationSettings}
                            >
                                Notification Settings
                            </button>
                        </div>

                    </div>
                </Col>
            </Row>

            <Row className="mb-4">
                <Col md={3}>
                    <div className="anm-kpi-card total">
                        <div className="anm-kpi-title">
                            Total Issues (
                            {activeStore || "All Stores"}
                            )
                        </div>

                        <div className="anm-kpi-value">
                            {kpi.total.toLocaleString()}
                        </div>
                    </div>
                </Col>

                <Col md={3}>
                    <div className="anm-kpi-card critical">
                        <div className="anm-kpi-title">
                            Critical
                        </div>

                        <div className="anm-kpi-value">
                            {kpi.criticalCount}
                        </div>
                    </div>
                </Col>


                <Col md={3}>
                    <div className="anm-kpi-card high">
                        <div className="anm-kpi-title">
                            High
                        </div>

                        <div className="anm-kpi-value">
                            {kpi.highCount}
                        </div>
                    </div>
                </Col>


                <Col md={3}>
                    <div className="anm-kpi-card needs-attention">
                        <div className="anm-kpi-title">
                            Needs Attention
                        </div>
                        <div className="anm-kpi-value">
                            {kpi.needsAttentionCount}
                        </div>
                    </div>
                </Col>
            </Row>

            <div className="anm-card">
                <Row className="anm-card-header align-items-end justify-content-end">
                    <Col md={12} className="d-flex align-items-end justify-content-end">

                        <div className="text-right">

                            <select
                                className="form-control"
                                value={selectedStoreName}
                                onChange={handleStoreChange}
                            >
                                <option value="" disabled>
                                    --Select Store--
                                </option>
                                {selectedStoreName &&
                                    !(storeOptions || []).some(
                                        (s) => s.name === selectedStoreName
                                    ) && (
                                        <option value={selectedStoreName}>
                                            {selectedStoreName}
                                        </option>
                                    )}
                                {storeOptions.map(
                                    (s) => (

                                        <option
                                            key={s.integerID ? `${s.integerID}-${s.name}` : s.name}
                                            value={s.name}
                                        >
                                            {s.location ? `${s.name} (${s.location})` : s.name}
                                        </option>

                                    )
                                )}

                            </select>

                        </div>

                    </Col>

                </Row>

            </div>

            <LoadingOverlay
                active={isLoading}
                spinner={
                    <Loader
                        active
                        type="ball-pulse"
                    />
                }
                text="Loading Amazon Notifications..."
                styles={{
                    overlay: (base) => ({
                        ...base,
                        background: "#f1f4f6",
                        opacity: 1
                    }),
                    content: (base) => ({
                        ...base,
                        color: "#000"
                    })
                }}
            >

                <div className="anm-card">

                    <Row className="anm-card-header align-items-center">

                        <Col md={8}>

                            <h3>
                                ASIN / SKU / Seller ID-Level
                                Issue Summary
                                {activeStore
                                    ? ` for ${activeStore}`
                                    : ""}
                            </h3>

                        </Col>

                        <Col md={4} className="text-right">

                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search ASIN, SKU, Seller ID..."
                                value={itemSummarySearch}
                                onChange={(e) =>
                                    setItemSummarySearch(e.target.value)
                                }
                            />

                        </Col>

                    </Row>

                    <ReactTable
                        data={filteredItemSummary}
                        columns={itemSummaryColumns}
                        className="-fixed -highlight -striped mt-2"
                        getTheadProps={() => ({
                            style: {
                                overflowY: "hidden",
                                background: "#B1FA63",
                                color: "#243837",
                            },
                        })}
                        getNoDataProps={() => ({
                            style: {
                                display: "none",
                            },
                        })}
                        getTrProps={(state, rowInfo) => {
                            if (!rowInfo) {
                                return {};
                            }
                            const isSelected =
                                selectedItemKey &&
                                rowInfo.original.rowKey ===
                                selectedItemKey;
                            return {
                                className: isSelected
                                    ? "anm-item-row selected"
                                    : "anm-item-row",
                                onClick: () => {
                                    const nextItem =
                                        selectedItemKey ===
                                        rowInfo.original.rowKey
                                            ? null
                                            : rowInfo.original;
                                    setSelectedItemKey(
                                        nextItem
                                            ? nextItem.rowKey
                                            : null
                                    );
                                    setCurrentPage(0);
                                    setSearch("");
                                    fetchIssues(
                                        nextItem,
                                        0,
                                        "",
                                        selectedStoreIntegerID,
                                        notificationTab,
                                        { showOverlay: true }
                                    );
                                    if (nextItem && issuesSectionRef.current) {
                                        issuesSectionRef.current.scrollIntoView({
                                            behavior: "smooth",
                                            block: "start",
                                        });
                                    }
                                },
                            };
                        }}
                        defaultPageSize={10}
                        minRows={0}
                        noDataText=""
                    />

                </div>

                <div className="anm-card" ref={issuesSectionRef}>

                    <Row className="anm-card-header align-items-center">

                        <Col md={8}>

                            <h3>
                                Notification Issues
                            </h3>

                        </Col>

                    </Row>

                    <div className="anm-filter-toolbar">

                        <div className="anm-filter-row">

                            <input
                                type="text"
                                className="form-control w-25"
                                placeholder="Search ASIN, SKU, Seller ID, Notification type..."
                                value={search}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setSearch(value);
                                    setCurrentPage(0);
                                    if (issuesSearchTimerRef.current) {
                                        clearTimeout(issuesSearchTimerRef.current);
                                    }
                                    issuesSearchTimerRef.current = setTimeout(() => {
                                        const q = (value || "").trim();
                                        const selectedItem =
                                            (itemSummary || []).find(
                                                (item) =>
                                                    item.rowKey ===
                                                    selectedItemKey
                                            ) || null;
                                        fetchIssues(
                                            selectedItem,
                                            0,
                                            q,
                                            selectedStoreIntegerID,
                                            notificationTab,
                                            { showOverlay: true }
                                        );
                                    }, 300);
                                }}
                            />

                            <select
                                className="form-control"
                                value={
                                    filterSeverity
                                }
                                onChange={(e) =>
                                    setFilterSeverity(
                                        e.target.value
                                    )
                                }
                                style={{
                                    color: !filterSeverity
                                        ? "#6c757d"
                                        : "#050505",
                                }}
                            >

                                <option value="">
                                    -- Select Severity --
                                </option>

                                {getLookupOptions(
                                    noficationsActions,
                                    "Amazon_Notifications_Severity"
                                ).map((a) => {
                                    const label = getLookupLabel(
                                        noficationsActions,
                                        "Amazon_Notifications_Severity",
                                        a.key
                                    );
                                    return (
                                        <option
                                            key={a.id || a.key}
                                            value={label}
                                        >
                                            {label}
                                        </option>
                                    );
                                })}

                            </select>

                            <select
                                className="form-control"
                                value={filterBusinessImpact}
                                onChange={(e) =>
                                    setFilterBusinessImpact(
                                        e.target.value
                                    )
                                }
                                style={{
                                    color: !filterBusinessImpact
                                        ? "#6c757d"
                                        : "#050505",
                                }}
                            >
                                <option value="">
                                    -- Business Impact --
                                </option>
                                {getLookupOptions(
                                    noficationsActions,
                                    "Amazon_Notifications_Business_Impact"
                                ).map((a) => {
                                    const label = getLookupLabel(
                                        noficationsActions,
                                        "Amazon_Notifications_Business_Impact",
                                        a.key
                                    );
                                    return (
                                        <option
                                            key={a.id || a.key}
                                            value={label}
                                        >
                                            {label}
                                        </option>
                                    );
                                })}
                            </select>

                            <select
                                className="form-control"
                                value={
                                    filterNeedsAttention
                                }
                                onChange={(e) =>
                                    setFilterNeedsAttention(
                                        e.target.value
                                    )
                                }
                                style={{
                                    color: !filterNeedsAttention
                                        ? "#6c757d"
                                        : "#050505",
                                }}
                            >

                                <option value="">
                                    -- Needs Attention --
                                </option>

                                {getLookupOptions(
                                    noficationsActions,
                                    "Amazon_Notifications_Attention"
                                ).map((a) => {
                                    const label = getLookupLabel(
                                        noficationsActions,
                                        "Amazon_Notifications_Attention",
                                        a.key
                                    );
                                    return (
                                        <option
                                            key={a.id || a.key}
                                            value={label}
                                        >
                                            {label}
                                        </option>
                                    );
                                })}

                            </select>

                            {/* <select
                                className="form-control"
                                value={filterStatus}
                                onChange={(e) =>
                                    setFilterStatus(
                                        e.target.value
                                    )
                                }
                                style={{
                                    color: !filterStatus
                                        ? "#6c757d"
                                        : "#050505",
                                }}
                            >

                                <option value="">
                                    -- Select Status --
                                </option>

                                <option value="NEW">
                                    New
                                </option>

                                <option value="OPEN">
                                    Open
                                </option>

                                <option value="IN PROGRESS">
                                    In Progress
                                </option>

                                <option value="RESOLVED">
                                    Resolved
                                </option>

                                <option value="INFORMATIONAL">
                                    Informational
                                </option>

                            </select> */}

                            <select
                                className="form-control"
                                value={filterDate}
                                onChange={(e) =>
                                    setFilterDate(
                                        e.target.value
                                    )
                                }
                                style={{
                                    color: !filterDate
                                        ? "#6c757d"
                                        : "#050505",
                                }}
                            >

                                <option value="">
                                    -- Select Date --
                                </option>

                                <option value="today">
                                    Today
                                </option>

                                <option value="7days">
                                    Last 7 Days
                                </option>
                            </select>
                            <select
                                className="form-control"
                                value={filterType}
                                onChange={(e) =>
                                    setFilterType(
                                        e.target.value
                                    )
                                }
                                style={{ color: !filterType ? "#6c757d" : "#050505", }}>
                                <option value="">-- Notification Types --</option>
                                {uniqueTypes.map(
                                    (type) => (
                                        <option
                                            key={type}
                                            value={type}
                                        >
                                            {type}
                                        </option>
                                    )
                                )}
                            </select>
                        </div>
                    </div>
                    <ReactTable
                        data={filteredIssues}
                        columns={issueColumns}
                        className="-fixed -highlight -striped mt-2"
                        getTheadProps={() => ({
                            style: {
                                overflowY: "hidden",
                                background: "#B1FA63",
                                color: "#243837",
                            },
                        })}
                        getNoDataProps={() => ({
                            style: {
                                display: "none",
                            },
                        })}
                        defaultPageSize={5}
                        minRows={0}
                        noDataText=""
                    />
                </div>
            </LoadingOverlay>

            {drawerItem && (
                <CustomModal
                    header="Notification Details"
                    isOpen={!!drawerItem}
                    toggle={() => setDrawerItem(null)}
                    size="xl"
                    className="curve-popup"
                >
                    <NotificationDetailsDrawer
                        item={drawerItem}
                        guide={guide}
                        actions={noficationsActions}
                        onClose={() =>
                            setDrawerItem(null)
                        }
                        onUpdateStatus={
                            updateIssueStatus
                        }
                    />
                </CustomModal>
            )}
            {payloadItem && (
                <CustomModal
                    header="Notification Payload"
                    isOpen={!!payloadItem}
                    toggle={() => setPayloadItem(null)}
                    size="xl"
                    className="curve-popup"
                >
                    <NotificationDetailsDrawer
                        item={payloadItem}
                        onlyPayload={true}
                    />
                </CustomModal>
            )}
            <CustomModal
                header="Amazon Notification Settings"
                isOpen={showHelp}
                toggle={() => setShowHelp(false)}
                size="xl"
                className="curve-popup"
            >
                <Row>
                    <Col md={12}>
                    <div className="brand-tabs d-flex align-items-center mb-3 w-100">
                            <div
                                className={`main-tabs ${
                                    settingsTab === "types" ? "active" : ""
                                }`}
                                onClick={() => setSettingsTab("types")}
                            >
                                Notification Types
                            </div>
                            <div
                                className={`main-tabs ${
                                    settingsTab === "matrix" ? "active" : ""
                                }`}
                                onClick={() => setSettingsTab("matrix")}
                            >
                                Classification Matrix Overview
                            </div>
                            <button
                                type="button"
                                className="btn btn-secondary btn-background-color-Proceed-gallery ml-auto"
                                disabled={
                                    isSavingSettings ||
                                    Object.keys(pendingLookupChanges).length === 0
                                }
                                onClick={handleSaveSettings}
                            >
                                Save
                            </button>
                        </div>
                        {settingsTab === "types" ? (
                            <ReactTable
                                data={settingsDraft || []}
                                columns={guideColumns}
                                className="-fixed -highlight -striped mt-2 anm-settings-table"
                                getTheadProps={() => ({
                                    style: {
                                        overflowY: "hidden",
                                        background: "#B1FA63",
                                        color: "#243837",
                                    },
                                })}
                                defaultPageSize={6}
                                noDataText="No Data Found."
                            />
                        ) : (
                            <ClassificationMatrixOverview />
                        )}
                    </Col>
                </Row>
            </CustomModal>
        </div>
    );
}

const mapStateToProps = (state) => ({
    token: state.loggedUser.token,
    adProfiles: state.accountOverview.landing.adProfiles
});
export default connect(mapStateToProps)(AmazonNotificationsDashboard);